import { CSSProperties, MutableRefObject, useEffect, useRef, useState } from 'react';
import {
  ReactZoomPanPinchContentRef,
  TransformComponent,
  TransformWrapper,
  useControls,
} from 'react-zoom-pan-pinch';
import { LoadingIndicator } from '@shared/components/loading';
import { API } from '@shared/types/api';
import { FileUpload } from '@shared/types/fieldValues';
import { useElementSize, useImageSrcAndDimensions } from '@shared/util/hooks';
import { MBZoomableImageProps } from '../props';
import { styles } from './styles';

interface InsideZoomableImageProps {
  image: FileUpload | API.ChatAttachmentFragment;
  containerDims: number[];
  isZoomed: boolean;
}

function InsideZoomableImage({ image, containerDims, isZoomed }: InsideZoomableImageProps) {
  const { setTransform } = useControls();

  const { src, width, height, resolved } = useImageSrcAndDimensions({
    image,
    containerWidth: containerDims[0],
    containerHeight: containerDims[1],
  });

  useEffect(() => {
    // Don't reposition while the user is zoomed in — dimension changes (e.g. the
    // post-EXIF orientation correction landing) must not reset the transform and snap the view back to scale=1.
    // When isZoomed transitions back to false, this effect re-runs and re-centers.
    if (isZoomed) {
      return;
    }
    const x = (containerDims[0] - width) / 2;
    const y = (containerDims[1] - height) / 2;

    setTransform(x, y, 1, 0);
  }, [setTransform, containerDims, width, height, isZoomed]);

  return (
    <TransformComponent wrapperStyle={{ width: '100%', height: '100%' }}>
      <img
        src={src}
        style={{
          width,
          height,
          opacity: resolved ? 1 : 0,
          transition: 'opacity 150ms ease-in',
        }}
        alt={image.name}
      />
      {!resolved && (
        <div style={styles.loadingContainer as CSSProperties}>
          <LoadingIndicator fill center padding={4} />
        </div>
      )}
    </TransformComponent>
  );
}

/*
    Simple wrapper around the image in the preview. The reason we split this into
    separate files for native and web is because of the native specific things
    happening in content.native.tsx
*/
export function MBZoomableImage({
  image,
  shouldHaveHorizontalMargins,
  onZoomStart,
  onZoomEnd,
}: MBZoomableImageProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const zoomRef = useRef<ReactZoomPanPinchContentRef | null>(null);
  const containerDims = useElementSize(containerRef as MutableRefObject<HTMLElement>);
  const [isZoomed, setIsZoomed] = useState(false);
  const prevIsZoomedRef = useRef(false);

  // Notify parent when zoomed state transitions so it can show/hide nav controls.
  useEffect(() => {
    if (isZoomed === prevIsZoomedRef.current) {
      return;
    }
    prevIsZoomedRef.current = isZoomed;
    if (isZoomed) {
      onZoomStart?.();
    } else {
      onZoomEnd?.();
    }
  }, [isZoomed, onZoomStart, onZoomEnd]);

  return (
    <>
      <div
        ref={containerRef}
        style={{
          ...styles.imageContainer,
          left: shouldHaveHorizontalMargins ? 100 : 0,
          right: shouldHaveHorizontalMargins ? 100 : 0,
          position: 'absolute',
        }}
      >
        <TransformWrapper
          ref={zoomRef}
          // don't allow panning when not zoomed,
          // so carousel swipe will work properly
          panning={{ disabled: !isZoomed }}
          onZoom={ref => {
            setIsZoomed(ref.state.scale > 1);
          }}
        >
          <InsideZoomableImage image={image} containerDims={containerDims} isZoomed={isZoomed} />
        </TransformWrapper>
      </div>
    </>
  );
}
